import React from "react";
import { useQuery } from "@apollo/client";
import { relatedNodesQuery } from "./related-nodes-query.graphql";

import TeaserParticipantGeneralRelated from "@default/teasers/participant-general/teaser-participant-general-related";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import Link from "./../../../general-components/link/link";
import LoadingIndicator from "@default/general-components/loading-indicator";
import { useNavigate } from "react-router-dom";
import PillBorder from "@default/general-components/pill-border";

import PropTypes from "prop-types";
import appearancesInPast from "@default/lib/appearances-in-past";

const Related = ({ content, type }) => {
  if (content.fieldAppearances) {
    const mainSettings = useSelector(
        (reduxStore) => reduxStore.appStore.mainSettings
      ),
      customHistory = useSelector(
        (reduxStore) => reduxStore.appStore.customHistory
      ),
      navigate = useNavigate();

    // Check if User comes from Overview Page or not
    let userComesFromOverviewPage =
      (mainSettings?.fieldConferenceOverviewPage &&
        type === "event" &&
        mainSettings?.fieldConferenceOverviewPage?.uri?.path ===
          customHistory[customHistory.length - 2]?.pathname) ||
      (mainSettings?.fieldFestivalOverviewPage &&
        type === "act" &&
        mainSettings?.fieldFestivalOverviewPage?.uri?.path ===
          customHistory[customHistory.length - 2]?.pathname);

    /**
     * Get current event type, if parent is event.
     */
    const eventType = [content.fieldEventType?.id].filter(Boolean);

    /**
     * Get current genre, if parent is act.
     */
    const genre = content.fieldGenre?.length
      ? content.fieldGenre?.map((item) => item.id.toString())
      : [];

    /**
     * Get current edition.
     */
    const edition = content?.fieldEdition?.id;

    /**
     * Set the view id based on the type.
     */
    let view;
    if (type === "event") view = "related_events";
    if (type === "act") view = "related_acts";

    /**
     * Related nodes query.
     */
    const { data, loading, error } = useQuery(relatedNodesQuery, {
      variables: {
        view,
        excludedId: content.id,
        eventType,
        genre,
        edition,
      },
      skip: !content.id || !type,
    });

    // We need to unify this array here, because Drupal cannot unify it when there are to few appearances/participants. Happens only on first Release.
    const relatedContent =
      [...new Set(data?.entityById?.executable?.execute?.rows)] || [];

    if (error) {
      console.error(error);
      return null;
    }

    if (relatedContent?.length === 0 && !loading) {
      return null;
    }

    if (loading) {
      return <LoadingIndicator />;
    }

    if (relatedContent) {
      return (
        <div className="related-content" data-type={type}>
          <div className="container heading">
            <div className="row">
              <h3
                className={`col-12 ${
                  type === "event" ? "headline-l" : "headline-xl"
                }`}
              >
                <FormattedMessage id={"related"} />
              </h3>
            </div>
          </div>
          <div className={"container content max-width"}>
            <div className="row">
              <div
                className={`col-12 ${
                  type === "event" ? "related-events" : "related-acts"
                }`}
              >
                <div className="row teasers">
                  {relatedContent.map((content, contentIndex) => (
                    <React.Fragment key={contentIndex}>
                      <TeaserParticipantGeneralRelated
                        item={content}
                        index={contentIndex}
                      />
                    </React.Fragment>
                  ))}
                </div>
                {((mainSettings?.fieldConferenceOverviewPage &&
                  type === "event") ||
                  (mainSettings?.fieldFestivalOverviewPage &&
                    type === "act")) && (
                  <div
                    className={"overview-link d-flex justify-content-center"}
                  >
                    {userComesFromOverviewPage ? (
                      <button
                        className="btn btn-primary more"
                        onClick={() => navigate(-1)}
                      >
                        <PillBorder blurredBackdrop />
                        {type === "act" ? (
                          <span>
                            {mainSettings?.fieldFestivalOverviewPage.title}
                          </span>
                        ) : (
                          <span>
                            {mainSettings?.fieldConferenceOverviewPage.title}
                          </span>
                        )}
                      </button>
                    ) : (
                      <>
                        {type === "act" ||
                        content.fieldEventType?.name === "Arts & Word" ? (
                          <Link
                            link={mainSettings?.fieldFestivalOverviewPage}
                            forceSameTab={true}
                            className="btn btn-primary more headline-s"
                            pill={true}
                          />
                        ) : (
                          <Link
                            link={mainSettings?.fieldConferenceOverviewPage}
                            forceSameTab={true}
                            className="btn btn-primary more headline-s"
                            pill={true}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
  }
};

Related.propTypes = {
  /**
   * Content of the parent entity.
   */
  content: PropTypes.object.isRequired,
  /**
   * Type of the parent entity (act or event).
   */
  type: PropTypes.oneOf(["act", "event"]).isRequired,
};

export default Related;
